import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';

const ComparisonModal = ({ request, onClose, onVerify }) => {
  const [activeTab, setActiveTab] = useState('company');

  const originalData = request.original_data || {};
  const updatedData = request.updated_data || {};

  // Extract data by section
  const companyInfo = {
    original: originalData.company_info || {},
    updated: {
      company_name: updatedData.company_name,
      business_vertical: updatedData.business_vertical,
      contact_person_name: updatedData.contact_person_name,
      designation: updatedData.designation,
      email: updatedData.email,
      phone_number: updatedData.phone_number,
      website: updatedData.website,
      business_description: updatedData.business_description,
    },
  };

  const addressInfo = {
    original: originalData.addresses || {},
    updated: {
      registered_address: updatedData.registered_address,
      registered_city: updatedData.registered_city,
      registered_state: updatedData.registered_state,
      registered_pincode: updatedData.registered_pincode,
      supply_address: updatedData.supply_address,
      supply_city: updatedData.supply_city,
      supply_state: updatedData.supply_state,
      supply_pincode: updatedData.supply_pincode,
    },
  };

  const bankInfo = {
    original: originalData.bank_details || {},
    updated: {
      bank_name: updatedData.bank_name,
      branch_name: updatedData.branch_name,
      account_type: updatedData.account_type,
      ifsc_code: updatedData.ifsc_code,
      swift_code: updatedData.swift_code,
    },
  };

  const renderFieldComparison = (label, originalValue, updatedValue) => {
    const hasChange = originalValue !== updatedValue && updatedValue !== undefined;
    const displayOriginal = originalValue || 'Not specified';
    const displayUpdated = updatedValue !== undefined ? (updatedValue || 'Not specified') : displayOriginal;

    if (!hasChange && updatedValue === undefined) {
      return null; // Don't show unchanged fields
    }

    return (
      <div
        key={label}
        className={`border rounded-lg p-4 ${hasChange ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'}`}
      >
        <h4 className="font-medium text-gray-900 mb-3">{label}</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Current Information:</p>
            <p className="text-sm text-green-700 font-medium">{displayOriginal}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Updated Information:</p>
            <p className={`text-sm font-medium ${hasChange ? 'text-red-700' : 'text-gray-700'}`}>
              {displayUpdated}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (sectionData, fieldLabels) => {
    const fields = [];
    Object.keys(fieldLabels).forEach((key) => {
      const originalValue = sectionData.original[key];
      const updatedValue = sectionData.updated[key];
      if (updatedValue !== undefined || originalValue) {
        fields.push(renderFieldComparison(fieldLabels[key], originalValue, updatedValue));
      }
    });
    return fields.filter(Boolean).length > 0 ? (
      <div className="space-y-4">{fields.filter(Boolean)}</div>
    ) : (
      <p className="text-gray-500 text-center py-8">No changes in this section</p>
    );
  };

  const tabs = [
    { id: 'company', label: 'Company Information' },
    { id: 'address', label: 'Addresses' },
    { id: 'bank', label: 'Bank Details' },
  ];

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-200" onClick={onClose} />
      <div className="fixed inset-0 z-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[calc(95vh/var(--app-scale))] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                Review Changes - {request.vendor.company_name}
              </h3>
              <p className="text-sm text-gray-600 mt-1">Compare original and updated information</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 px-6">
            <div className="flex gap-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 border-b-2 font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'company' &&
              renderSection(companyInfo, {
                company_name: 'Company Name',
                business_vertical: 'Business Vertical',
                contact_person_name: 'Contact Person',
                designation: 'Designation',
                email: 'Email',
                phone_number: 'Phone Number',
                website: 'Website',
                business_description: 'Business Description',
              })}

            {activeTab === 'address' &&
              renderSection(addressInfo, {
                registered_address: 'Registered Address',
                registered_city: 'Registered City',
                registered_state: 'Registered State',
                registered_pincode: 'Registered Pincode',
                supply_address: 'Supply Address',
                supply_city: 'Supply City',
                supply_state: 'Supply State',
                supply_pincode: 'Supply Pincode',
              })}

            {activeTab === 'bank' &&
              renderSection(bankInfo, {
                bank_name: 'Bank Name',
                branch_name: 'Branch Name',
                account_type: 'Account Type',
                ifsc_code: 'IFSC Code',
                swift_code: 'SWIFT Code',
              })}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={onVerify} className="bg-emerald-600 hover:bg-emerald-700">
              <Icon name="CheckCircle" size={18} className="mr-2" />
              Verify & Approve
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ComparisonModal;
